import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { FooterRow } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import DeleteIcon from "@mui/icons-material/Delete";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import EditIcon from "@mui/icons-material/Edit";
import FooterRowModal from "../../components/ScreenFooter/Footer/FooterRowModal/FooterRowModal";
import sanitizeHtml from "sanitize-html-react";

const Footer: React.FC = observer(() => {
  const { configStore } = useStores();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [footerRow, setFooterRow] = useState<FooterRow | undefined>(undefined);

  const modalHandler = useCallback(() => {
    if (isModalOpen) {
      setFooterRow(undefined);
    }
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const fetchFooterRows = useCallback(async () => {
    await configStore.fetchFooterRows().catch((err) => {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
    });
  }, [enqueueSnackbar, configStore]);

  const handleSubmit = useCallback(
    async (data: FooterRow) => {
      try {
        data.html = sanitizeHtml(data.html);
        if (footerRow) {
          await configStore.updateFooterRow(data);
        } else {
          await configStore.addFooterRow(data);
        }
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [enqueueSnackbar, footerRow, configStore, modalHandler, t]
  );

  const handleDeleteFooterRow = useCallback(
    async (id: number) => {
      try {
        await configStore.deleteFooterRow(id);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, configStore, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.footer.column.orderNumber"),
        dataKey: "orderNumber",
      },
      {
        width: 80,
        flexGrow: 3,
        label: t("screens.footer.column.html"),
        dataKey: "html",
        render: (html: string) => (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ),
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: number, footerRow: FooterRow) => {
          return (
            <>
              <IconButton
                key={id}
                onClick={() => {
                  setFooterRow(footerRow);
                  modalHandler();
                }}
              >
                <EditIcon />
              </IconButton>
              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteFooterRow(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDeleteFooterRow, modalHandler, t]
  );

  useEffect(() => {
    fetchFooterRows();
  }, [fetchFooterRows, configStore]);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.footer.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.footer.breadcrumb.footer")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.footer.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.footer.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {configStore.isLoading ? (
        <Loading />
      ) : configStore.footerRows.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(configStore.footerRows.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={configStore.footerRows.length}
              rowGetter={({ index }) => configStore.footerRows[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.footer.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.footer.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <FooterRowModal
        footerRow={footerRow}
        isSubmitting={false}
        isOpen={isModalOpen}
        onClose={modalHandler}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
});

export default Footer;
